import React, { useState, useEffect } from 'react';
import { Button } from 'src/components/ui/button/button';
import { useToast } from 'src/components/ui/use-toast';
import { useVideoAnalysis } from 'src/hooks/useVideoAnalysis';
import { useContentState } from 'src/hooks/useContentState';
import { Api } from 'src/api';
import { Logger } from 'src/utils/logger';
import { VideoAnalysisTriggerRequest } from 'src/services/video-analysis/video-analysis.service';
import { Loader2, FileText, Instagram, Mail, Check } from 'lucide-react';
import { ContentGenerationProgressModal } from 'src/components/modals/ContentGenerationProgressModal';
import { ContentDisplayModal } from 'src/components/modals/ContentDisplayModal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';
import { useBrandSelection } from 'src/providers/BrandSelectionProvider';
import { BrandOnboardingRequiredDialog } from 'src/components/dialogs/brand-onboarding-required-dialog';
import { cn } from 'src/lib/utils';

interface ContentGenerationButtonsProps {
  mediaId: string;
  requestId?: string;
  className?: string;
}

export const ContentGenerationButtons: React.FC<ContentGenerationButtonsProps> = ({
  mediaId,
  requestId,
  className = '',
}) => {
  const { generateContent, isGenerating, cancel } = useVideoAnalysis();
  const { toast } = useToast();
  const contentState = useContentState();
  const [currentContentType, setCurrentContentType] = useState<VideoAnalysisTriggerRequest['request_type'] | null>(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<string>('');
  const [showContentModal, setShowContentModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showOnboardingDialog, setShowOnboardingDialog] = useState(false);
  
  // Check if user has a brand selected
  const { selectedBrand } = useBrandSelection();

  // Feature is gated if no brand selected
  const isGated = !selectedBrand;

  // Load existing content when component mounts or mediaId changes
  useEffect(() => {
    if (mediaId) {
      contentState.loadContent(mediaId);
    }
  }, [mediaId]);

  // Get video URL for content generation using direct URLs
  const getVideoUrl = async (mediaId: string): Promise<string> => {
    try {
      let targetVideoUrl: string | undefined;
      
      // Get direct temporary URLs from API
      try {
        const mediaData = await Api.media.getTemporaryUrls(mediaId);
        const resolutions = mediaData?.resolutions;
        targetVideoUrl =
          mediaData?.url ||
          (resolutions && typeof resolutions === 'object'
            ? (resolutions as Record<string, string>)['720p'] ||
              (resolutions as Record<string, string>)['480p'] ||
              (resolutions as Record<string, string>)['360p']
            : undefined);
      } catch (error) {
        Logger.error('Failed to get media URL for video analysis', 'ContentGenerationButtons', error);
      }

      if (!targetVideoUrl) {
        throw new Error('Unable to get video URL. Please ensure this is a video file.');
      }

      return targetVideoUrl;
    } catch (error) {
      Logger.error('Failed to get video URL for content generation', 'ContentGenerationButtons', error);
      throw error;
    }
  };


  // Handle button click - check for existing content first
  const handleButtonClick = async (contentType: VideoAnalysisTriggerRequest['request_type']) => {
    // Check if no brand or legacy brand - show onboarding dialog
    if (isGated) {
      setShowOnboardingDialog(true);
      return;
    }
    
    const existingContent = contentState.getContent(mediaId, contentType);
    
    if (existingContent) {
      // Show existing content in view mode first
      setCurrentContentType(contentType);
      setGeneratedContent(existingContent);
      setIsEditMode(false);
      setShowContentModal(true);
    } else {
      // Generate new content
      await handleContentGeneration(contentType);
    }
  };

  // Handle content generation for any content type
  const handleContentGeneration = async (
    contentType: VideoAnalysisTriggerRequest['request_type'],
    options?: { force?: boolean },
  ) => {
    try {
      // Set current content type and show progress modal
      setCurrentContentType(contentType);
      setShowProgressModal(true);

      // Get video URL
      const videoUrl = await getVideoUrl(mediaId);

      // Generate content. `mediaId` is threaded through so the BE row gets a
      // stable key for cross-session retrieval; `force` is only set on
      // explicit Regenerate so normal clicks reuse cached results.
      const content = await generateContent(videoUrl, contentType, {
        mediaId,
        force: options?.force,
      });
      
      // Handle cancellation - if empty content returned, just close progress modal
      if (!content || content.trim() === '') {
        setShowProgressModal(false);
        setCurrentContentType(null);
        return; // Exit without showing content modal
      }
      
      // Save generated content to state and persist to localStorage
      contentState.setContent(mediaId, contentType, content);
      contentState.saveContent(mediaId, contentType, content);
      
      // Hide progress modal and show content modal
      setShowProgressModal(false);
      setGeneratedContent(content);
      setIsEditMode(false);
      setShowContentModal(true);
    } catch (error: any) {
      // Hide progress modal on error
      setShowProgressModal(false);
      setCurrentContentType(null);
      
      // Error handling - toast is already shown by useVideoAnalysis hook for generation errors
      if (error?.message?.includes('video URL')) {
        toast({
          description: 'Unable to get video URL. Please ensure this is a video file.',
          variant: 'destructive',
        });
      }
      // Other errors are already handled by useVideoAnalysis hook
    }
  };

  // Handle modal cancel
  const handleProgressModalCancel = () => {
    cancel();
    setShowProgressModal(false);
    setCurrentContentType(null);
  };

  // Handle content modal close — persist content so green checkmark survives remount
  const handleContentModalClose = () => {
    if (currentContentType && generatedContent) {
      contentState.saveContent(mediaId, currentContentType, generatedContent);
    }
    setShowContentModal(false);
    setGeneratedContent('');
    setCurrentContentType(null);
    setIsEditMode(false);
  };

  // Handle save content
  const handleSaveContent = async (editedContent: string): Promise<boolean> => {
    if (!currentContentType) return false;
    
    const success = await contentState.saveContent(mediaId, currentContentType, editedContent);
    if (success) {
      setGeneratedContent(editedContent);
    }
    return success;
  };

  // Handle regenerate content
  const handleRegenerateContent = async () => {
    if (!currentContentType) return;

    setShowContentModal(false);
    await handleContentGeneration(currentContentType, { force: true });
  };

  // Per-button disable: only block clicks when there's no cached content for
  // *this* type AND we're still hydrating. Once a button has a green tick the
  // click just opens the cached modal, so there's no race to guard against.
  const isHydrating = contentState.isLoading(mediaId);
  const buttonDisabled = (type: VideoAnalysisTriggerRequest['request_type']) =>
    isGenerating || (isHydrating && !contentState.hasContent(mediaId, type));

  return (
    <TooltipProvider>
      <div className={`tw-flex tw-items-center tw-gap-2 ${className}`}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => handleButtonClick('blog')}
              disabled={buttonDisabled('blog')}
              size="sm"
              variant="outline"
              className={cn(
                'tw-gap-1.5 tw-h-9 tw-px-3 tw-py-2 tw-text-xs tw-font-medium hover:tw-bg-purple-50 hover:tw-border-purple-300',
                contentState.hasContent(mediaId, 'blog') && 'tw-bg-green-50 tw-border-green-300',
                isGated && 'tw-opacity-60 tw-cursor-not-allowed'
              )}
            >
              {isGenerating && currentContentType === 'blog' ? (
                <Loader2 className="tw-h-3.5 tw-w-3.5 tw-animate-spin" />
              ) : contentState.hasContent(mediaId, 'blog') ? (
                <Check className="tw-h-3.5 tw-w-3.5 tw-text-green-600" />
              ) : (
                <FileText className="tw-h-3.5 tw-w-3.5" />
              )}
              <span className="tw-hidden sm:tw-inline">Blog Post</span>
              <span className="tw-inline sm:tw-hidden">Blog</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" align="start">
            <p>Comprehensive blog article with SEO optimization, structured content, and engaging copy</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => handleButtonClick('ig_captions')}
              disabled={buttonDisabled('ig_captions')}
              size="sm"
              variant="outline"
              className={cn(
                'tw-gap-1.5 tw-h-9 tw-px-3 tw-py-2 tw-text-xs tw-font-medium hover:tw-bg-purple-50 hover:tw-border-purple-300',
                contentState.hasContent(mediaId, 'ig_captions') && 'tw-bg-green-50 tw-border-green-300',
                isGated && 'tw-opacity-60 tw-cursor-not-allowed'
              )}
            >
              {isGenerating && currentContentType === 'ig_captions' ? (
                <Loader2 className="tw-h-3.5 tw-w-3.5 tw-animate-spin" />
              ) : contentState.hasContent(mediaId, 'ig_captions') ? (
                <Check className="tw-h-3.5 tw-w-3.5 tw-text-green-600" />
              ) : (
                <Instagram className="tw-h-3.5 tw-w-3.5" />
              )}
              <span className="tw-hidden sm:tw-inline">IG Caption</span>
              <span className="tw-inline sm:tw-hidden">IG</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" align="start">
            <p>Social media ready caption with hashtags, engaging hooks, and call-to-actions</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => handleButtonClick('email_text')}
              disabled={buttonDisabled('email_text')}
              size="sm"
              variant="outline"
              className={cn(
                'tw-gap-1.5 tw-h-9 tw-px-3 tw-py-2 tw-text-xs tw-font-medium hover:tw-bg-purple-50 hover:tw-border-purple-300',
                contentState.hasContent(mediaId, 'email_text') && 'tw-bg-green-50 tw-border-green-300',
                isGated && 'tw-opacity-60 tw-cursor-not-allowed'
              )}
            >
              {isGenerating && currentContentType === 'email_text' ? (
                <Loader2 className="tw-h-3.5 tw-w-3.5 tw-animate-spin" />
              ) : contentState.hasContent(mediaId, 'email_text') ? (
                <Check className="tw-h-3.5 tw-w-3.5 tw-text-green-600" />
              ) : (
                <Mail className="tw-h-3.5 tw-w-3.5" />
              )}
              <span className="tw-hidden sm:tw-inline">Email</span>
              <span className="tw-inline sm:tw-hidden">Email</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" align="start">
            <p>Professional email content with subject line, preheader, and persuasive copy</p>
          </TooltipContent>
        </Tooltip>

        {/* Progress Modal */}
        <ContentGenerationProgressModal
          isOpen={showProgressModal}
          onClose={() => setShowProgressModal(false)}
          contentType={currentContentType}
          onCancel={handleProgressModalCancel}
        />

        {/* Content Display Modal */}
        <ContentDisplayModal
          isOpen={showContentModal}
          onClose={handleContentModalClose}
          content={generatedContent}
          contentType={currentContentType}
          isEditMode={isEditMode}
          onSave={handleSaveContent}
          onRegenerate={handleRegenerateContent}
          showRegenerateOption={true}
          isSaving={contentState.isLoading(mediaId)}
        />

        {/* Onboarding Required Dialog for Legacy Brands */}
        <BrandOnboardingRequiredDialog
          open={showOnboardingDialog}
          onOpenChange={setShowOnboardingDialog}
          brandId={selectedBrand?.id}
        />
      </div>
    </TooltipProvider>
  );
};

export type { ContentGenerationButtonsProps };
